import { Component, DestroyRef, Input, OnChanges, OnInit, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IContact, ISegment, ISegmentRule } from '../../../../../types/contactModule';
import { ContactsService } from '../../../../../services/contacts/contacts.service';
import { MessageService } from '../../../../../services/message/message.service';
import { SegmentService } from '../../../../../services/segments/segment-service.service';
import { GroupAudienceService, GroupRef, Reach } from '../../../../../services/segments/group-audience.service';
import {
  IEntitySegmentQuery,
  ReadFilter,
  describeFilter,
  filtersToRules,
  readFilters,
} from '../../../../../components/organization/segments/segment-utilities';
import {
  GroupContactsTableComponent,
  TMemberAction,
} from '../../../../../components/tables/group-contacts-table/group-contacts-table.component';
import { AssignMultipleContactsToOrganizationsComponent } from '../../../../../components/tables/assign-multiple-contacts-to-organizations/assign-multiple-contacts-to-organizations.component';
import { AssignMultipleContactsToTagsComponent } from '../../../../../components/tables/assign-multiple-contacts-to-tags/assign-multiple-contacts-to-tags.component';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { BackButtonComponent } from '../../../../../components/ui/back-button/back-button.component';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { formatCount, formatWhen } from '../../../../../../utils/sms';
import { countNoun } from '../../../../../components/tables/group-table/group-rows';

interface RuleLine {
  text: string;
  legacy: boolean;
}

@Component({
  selector: 'segment-details',
  standalone: true,
  imports: [
    GroupContactsTableComponent,
    AssignMultipleContactsToOrganizationsComponent,
    AssignMultipleContactsToTagsComponent,
    PromptCardComponent,
    BackButtonComponent,
    HasPermissionDirective,
  ],
  templateUrl: './segment-details.component.html',
  styleUrls: ['../../../../../components/tables/group-table/group-details.css', './segment-details.component.css'],
})
export class SegmentDetailsComponent implements OnChanges, OnInit {
  @ViewChild(GroupContactsTableComponent, { static: false })
  groupContactsTableChild?: GroupContactsTableComponent;

  private location = inject(Location);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private audience = inject(GroupAudienceService);
  private messageService = inject(MessageService);
  private segmentService = inject(SegmentService);
  contactservice = inject(ContactsService);

  /** The segment's uuid, from the route. */
  @Input() id!: string;

  readonly formatCount = formatCount;

  segmentDetail: IEntitySegmentQuery | null = null;
  segmentId: string | number = '';
  loading = true;
  loadError = '';
  private loadSub?: Subscription;
  private countSub?: Subscription;

  contactsCount: number | null = null;
  /** The member count did not load. */
  countError = false;
  reach: Reach | null = null;
  selectedContacts: IContact[] = [];

  /** The saved rules, read once per load. */
  segmentRules: ISegmentRule[] = [];
  ruleLines: RuleLine[] = [];
  ruleSentence = '';
  hasLegacyRules = false;

  // for multiple selected contacts
  showAssignMultipleContactsToOrg: boolean = false;
  showAssignMultipleContactsToTags: boolean = false;
  showDeleteSelectedContactsPrompt: boolean = false;
  deleting = false;

  get ref(): GroupRef | null {
    const segment = this.segmentDetail;
    return segment?.uuid ? { kind: 'segment', uuid: segment.uuid, id: segment.id ?? null } : null;
  }

  get name(): string {
    return (this.segmentDetail?.name ?? '').toString().trim() || 'Untitled segment';
  }

  get description(): string {
    return (this.segmentDetail?.description ?? '').toString().trim();
  }

  get createdBy(): string {
    return (this.segmentDetail?.createdBy?.login ?? '').toString().trim() || '—';
  }

  get created(): string {
    return formatWhen(this.segmentDetail?.createdAt);
  }

  get updated(): string {
    return formatWhen(this.segmentDetail?.modifiedAt || this.segmentDetail?.createdAt);
  }

  get formattedSegmentDetail() {
    //format the segment detail to be returned as an ISegment type
    return this.segmentDetail as ISegment;
  }

  get deleteTitle(): string {
    const count = this.selectedContacts.length;
    return count === 1 ? 'Delete this contact from your contacts?' : `Delete ${count} contacts from your contacts?`;
  }

  get deleteButton(): string {
    const count = this.selectedContacts.length;
    return count === 1 ? 'Delete contact' : `Delete ${count} contacts`;
  }

  // ── Loading ──
  loadSegment() {
    if (!this.id) return;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .getSegment(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (segment) => {
          this.segmentDetail = segment;
          this.segmentId = segment?.id ?? '';
          this.readRules(segment);
          this.loading = false;
          this.handleSegmentContactsCount();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'This segment did not load. It may have been deleted, or the connection dropped.';
        },
      });
  }

  private readRules(segment: IEntitySegmentQuery | null) {
    const read: ReadFilter[] = readFilters(segment?.entitySegmentQueryFilters);
    this.segmentRules = filtersToRules(segment?.entitySegmentQueryFilters);
    this.ruleLines = read.map((rule) => ({ text: describeFilter(rule), legacy: rule.legacy }));
    this.hasLegacyRules = read.some((rule) => rule.legacy);
    // "Contacts whose city contains “Gab” and age is more than 30 years."
    this.ruleSentence = read
      .filter((rule) => !rule.legacy)
      .map((rule) => {
        const text = describeFilter(rule);
        return text.charAt(0).toLowerCase() + text.slice(1);
      })
      .join(' and ');
  }

  /** Matching contacts today, then how many of them have no phone number. */
  handleSegmentContactsCount() {
    const ref = this.ref;
    const segment = this.segmentDetail;
    if (!ref || !segment) return;
    this.countError = false;
    // A newer count replaces one still in flight, so a slow, older reply never lands last.
    this.countSub?.unsubscribe();
    this.countSub = this.audience
      .memberCount(ref)
      .pipe(
        switchMap((count) => {
          this.contactsCount = count;
          return this.audience.segmentReach(segment, count);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (reach) => (this.reach = reach),
        error: () => {
          this.contactsCount = null;
          this.countError = true;
        },
      });
  }

  onMembersChanged() {
    this.audience.invalidate(this.ref);
    this.handleSegmentContactsCount();
  }

  // ── Header actions ──
  goBack() {
    this.location.back(); // Navigates back in the platform's history
  }

  /** Opens the composer with this segment already picked. */
  messageGroup() {
    if (!this.segmentDetail) return;
    this.messageService.updateSelectedSegments([this.segmentDetail]);
    this.router.navigate(['/organization/messages/create']);
  }

  editConditions() {
    if (!this.segmentDetail?.uuid) return;
    this.segmentService.startEditing(this.segmentDetail);
    this.router.navigate([`organization/contacts/segmented-list/edit/${this.segmentDetail.uuid}`], {
      queryParams: { title: 'rule' },
    });
  }

  // ── Members ──
  updateSelectedContacts(contacts: IContact[]) {
    this.selectedContacts = contacts;
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
    this.groupContactsTableChild?.unselectAll();
  }

  handleAction(action: TMemberAction | string) {
    if (this.selectedContacts.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'move-to-organization':
        this.openAssignMultipleContactsToOrg();
        break;
      case 'add-tags':
        this.openAssignMultipleContactsToTags();
        break;
      case 'delete':
        this.openDeleteSelectedContactsPrompt();
        break;
    }
  }

  sendSMStoSelectedContacts() {
    const params = { items: JSON.stringify(this.selectedContacts) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
    this.resetSelectedContacts();
  }

  openDeleteSelectedContactsPrompt() {
    if (this.selectedContacts.length > 0) {
      this.showDeleteSelectedContactsPrompt = true;
    }
  }

  closeDeleteSelectedContactsPrompt() {
    if (this.deleting) return;
    this.showDeleteSelectedContactsPrompt = false;
  }

  openAssignMultipleContactsToOrg() {
    if (this.selectedContacts.length > 0) {
      this.showAssignMultipleContactsToOrg = true;
    }
  }

  closeAssignMultipleContactsToOrg() {
    this.showAssignMultipleContactsToOrg = false;
  }

  openAssignMultipleContactsToTags() {
    if (this.selectedContacts.length > 0) {
      this.showAssignMultipleContactsToTags = true;
    }
  }

  closeAssignMultipleContactsToTags() {
    this.showAssignMultipleContactsToTags = false;
  }

  /** Deletes the ticked contacts from the whole contact list (not just this segment). */
  deleteSelectedContacts() {
    const count = this.selectedContacts.length;
    if (!count || this.deleting) return;
    this.deleting = true;
    this.contactservice.deleteSelectedContacts(this.selectedContacts.map((el) => el.uuid as string)).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeleteSelectedContactsPrompt = false;
        this.resetSelectedContacts();
        this.onMembersChanged();
        this.groupContactsTableChild?.load();
      },
      error: () => {
        this.deleting = false;
        this.showDeleteSelectedContactsPrompt = false;
        this.contactservice.showError(
          count === 1 ? 'The contact was not deleted. Try again.' : `The ${count} contacts were not deleted. Try again.`
        );
      },
    });
  }

  countNoun = countNoun;

  ngOnChanges() {
    // The route reuses this component when moving between segments.
    this.segmentDetail = null;
    this.countSub?.unsubscribe();
    this.contactsCount = null;
    this.reach = null;
    this.selectedContacts = [];
    this.loadSegment();
  }

  ngOnInit(): void {
    if (!this.loadSub) this.loadSegment();
  }
}
